import { Group, ImagePlus, MessageSquarePlus, Redo2, StickyNote, Undo2 } from 'lucide-react'
import { type ReactNode, useRef } from 'react'
import { useI18n } from '@/i18n/i18n'

function Tool({
  label,
  shortcut,
  onClick,
  disabled,
  active,
  children,
}: {
  label: string
  shortcut?: string
  onClick: () => void
  disabled?: boolean
  active?: boolean
  children: ReactNode
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={shortcut ? `${label} (${shortcut})` : label}
      disabled={disabled}
      aria-pressed={active}
      onClick={onClick}
      className="grid size-9 place-items-center rounded-md text-ink-2 transition-colors hover:bg-surface-2 hover:text-ink disabled:text-muted/50 disabled:hover:bg-transparent aria-pressed:bg-ai-soft aria-pressed:text-ai"
    >
      {children}
    </button>
  )
}

export function Toolbar(props: {
  canUndo: boolean
  canRedo: boolean
  hasSelection: boolean
  groupActive: boolean
  onCard: () => void
  onGroup: () => void
  /** Say something about the selection. */
  onSayAbout: () => void
  onFiles: (files: File[]) => void
  onUndo: () => void
  onRedo: () => void
}) {
  const { t } = useI18n()
  const input = useRef<HTMLInputElement>(null)
  const mod = navigator.platform.toLowerCase().includes('mac') ? '⌘' : 'Ctrl+'
  return (
    <div
      role="toolbar"
      aria-label={t('toolbar.label')}
      className="absolute bottom-5 left-1/2 z-10 flex -translate-x-1/2 items-center max-sm:right-3 max-sm:left-auto max-sm:translate-x-0 gap-0.5 rounded-lg border border-line bg-(--chrome) p-1 shadow-lg backdrop-blur-xl backdrop-saturate-150"
    >
      <Tool label={t('toolbar.card')} shortcut="C" onClick={props.onCard}>
        <StickyNote size={18} strokeWidth={1.75} />
      </Tool>
      <Tool
        label={props.hasSelection ? t('toolbar.groupSelection') : t('toolbar.group')}
        shortcut="G"
        onClick={props.onGroup}
        active={props.groupActive}
      >
        <Group size={18} strokeWidth={1.75} />
      </Tool>
      <Tool
        label={t('toolbar.sayAbout')}
        shortcut="M"
        onClick={props.onSayAbout}
        disabled={!props.hasSelection}
      >
        <MessageSquarePlus size={18} strokeWidth={1.75} />
      </Tool>
      <Tool
        label={t('toolbar.upload')}
        onClick={() => {
          input.current?.click()
        }}
      >
        <ImagePlus size={18} strokeWidth={1.75} />
      </Tool>
      <input
        ref={input}
        type="file"
        multiple
        hidden
        onChange={(e) => {
          const files = [...(e.target.files ?? [])]
          e.target.value = ''
          if (files.length) props.onFiles(files)
        }}
      />
      <span className="mx-1 h-5 w-px bg-line" />
      <Tool
        label={t('toolbar.undo')}
        shortcut={`${mod}Z`}
        onClick={props.onUndo}
        disabled={!props.canUndo}
      >
        <Undo2 size={17} strokeWidth={1.75} />
      </Tool>
      <Tool
        label={t('toolbar.redo')}
        shortcut={`${mod}⇧Z`}
        onClick={props.onRedo}
        disabled={!props.canRedo}
      >
        <Redo2 size={17} strokeWidth={1.75} />
      </Tool>
    </div>
  )
}
